export interface FileNode {
	name: string;
	type: 'file' | 'directory';
	path: string;
	children?: FileNode[];
	expanded?: boolean;
	childrenLoaded?: boolean;
}

export type WorkspaceFile = { name: string; type: 'file' | 'directory'; path: string };

export function sortWorkspaceFiles(files: WorkspaceFile[]): WorkspaceFile[] {
	return [...files].sort((a, b) => {
		if (a.type !== b.type) return a.type === 'directory' ? -1 : 1;
		return a.name.localeCompare(b.name);
	});
}

/**
 * Builds a flat list of root-level FileNodes without recursing into subdirectories.
 * Used after a single-level root listing so children can be loaded lazily.
 */
export function buildRootNodes(files: WorkspaceFile[]): FileNode[] {
	return sortWorkspaceFiles(files).map(file => ({
		name: file.name,
		type: file.type,
		path: file.path,
		children: file.type === 'directory' ? [] : undefined,
		expanded: false,
		childrenLoaded: file.type !== 'directory',
	}));
}

/**
 * Builds a fully-nested FileNode tree from a flat recursive file listing.
 * All directories are marked childrenLoaded: true since we have the complete data.
 */
export function buildFileTree(files: WorkspaceFile[]): FileNode[] {
	const tree: FileNode[] = [];
	const pathMap = new Map<string, FileNode>();

	for (const file of sortWorkspaceFiles(files)) {
		const parts = file.path.split('/').filter(p => p);
		let currentPath = '';
		let parentNode: FileNode | null = null;

		for (let i = 0; i < parts.length; i++) {
			currentPath = parts.slice(0, i + 1).join('/');
			const isLast = i === parts.length - 1;

			if (!pathMap.has(currentPath)) {
				const node: FileNode = {
					name: parts[i],
					type: isLast ? file.type : 'directory',
					path: currentPath,
					children: [],
					expanded: false,
					childrenLoaded: true,
				};

				pathMap.set(currentPath, node);

				if (parentNode) {
					/* istanbul ignore next -- parentNode.children is always initialised to [] during node creation */
					if (!parentNode.children) parentNode.children = [];
					parentNode.children.push(node);
				} else {
					tree.push(node);
				}
			}

			parentNode = pathMap.get(currentPath)!;
		}
	}

	return tree;
}

/**
 * Merges a fully-loaded tree into an existing (possibly partial) tree,
 * preserving the expanded state of any nodes the user has already interacted with.
 */
export function mergeTree(existing: FileNode[], updated: FileNode[]): FileNode[] {
	const existingMap = new Map<string, FileNode>();

	function buildMap(nodes: FileNode[]): void {
		for (const node of nodes) {
			existingMap.set(node.path, node);
			if (node.children) buildMap(node.children);
		}
	}
	buildMap(existing);

	function merge(nodes: FileNode[]): FileNode[] {
		return nodes.map(node => {
			const existingNode = existingMap.get(node.path);
			return {
				...node,
				expanded: existingNode?.expanded ?? false,
				children: node.children ? merge(node.children) : undefined,
			};
		});
	}

	return merge(updated);
}

export function updateNodeExpanded(nodes: FileNode[], targetPath: string): FileNode[] {
	return nodes.map(node => {
		if (node.path === targetPath) return { ...node, expanded: !node.expanded };
		if (node.children) return { ...node, children: updateNodeExpanded(node.children, targetPath) };
		/* istanbul ignore next -- all FileNodes are created with children:[] so this branch is unreachable */
		return node;
	});
}

/**
 * Sets children on a specific directory node. If the node is already marked
 * childrenLoaded (e.g. the prefetch beat the on-demand call), the existing data
 * is preserved to avoid replacing a deeper recursive result with a shallower one.
 */
export function setNodeChildren(nodes: FileNode[], targetPath: string, children: FileNode[]): FileNode[] {
	return nodes.map(node => {
		if (node.path === targetPath) {
			if (node.childrenLoaded) return node;
			return { ...node, children, childrenLoaded: true };
		}
		if (node.children) return { ...node, children: setNodeChildren(node.children, targetPath, children) };
		/* istanbul ignore next -- all directory FileNodes are created with children:[] */
		return node;
	});
}

export function findNodeByPath(nodes: FileNode[], targetPath: string): FileNode | null {
	for (const node of nodes) {
		if (node.path === targetPath) return node;
		if (node.children) {
			const found = findNodeByPath(node.children, targetPath);
			if (found) return found;
		}
	}
	/* istanbul ignore next -- callers only pass paths that exist in the rendered tree */
	return null;
}
